import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatNumber } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import ExportButtons from "@/components/app/ExportButtons";
import { Plus, X, Trash2, Pencil, TrendingUp, TrendingDown, Wallet, Truck, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell
} from "recharts";

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (m) => {
  if (!m || m === "unknown") return "-";
  const [y, mo] = m.split("-");
  const names = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
  return `${names[parseInt(mo, 10) - 1] || mo} ${y?.slice(2)}`;
};

export default function VehicleEarningsPage() {
  const [earnings, setEarnings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [summary, setSummary] = useState({ monthly: [], totals: {} });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ vehicle_id: "", month: currentMonth(), amount: 0, description: "" });

  // Yearly XLSX upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [replaceYear, setReplaceYear] = useState(false);

  // Monthly Details upload state
  const [showDetails, setShowDetails] = useState(false);
  const [detFile, setDetFile] = useState(null);
  const [detPreview, setDetPreview] = useState(null);
  const [detLoading, setDetLoading] = useState(false);
  const [detReplace, setDetReplace] = useState(false);

  const load = async () => {
    try {
      const [e, v, s] = await Promise.all([
        api.get("/vehicle-earnings"),
        api.get("/vehicles"),
        api.get("/monthly-earnings/summary"),
      ]);
      setEarnings(e.data);
      setVehicles(v.data);
      setSummary(s.data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ vehicle_id: "", month: currentMonth(), amount: 0, description: "" }); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ vehicle_id: r.vehicle_id, month: r.month, amount: r.amount, description: r.description || "" }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) || 0 };
    try {
      if (editing) await api.put(`/vehicle-earnings/${editing.id}`, payload);
      else await api.post("/vehicle-earnings", payload);
      toast.success("Kaydedildi");
      setShowForm(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm("Bu kazanç kaydını silmek istiyor musunuz?")) return;
    try { await api.delete(`/vehicle-earnings/${r.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const openUpload = () => {
    setUploadFile(null); setUploadPreview(null); setReplaceYear(false);
    setUploadYear(new Date().getFullYear()); setShowUpload(true);
  };
  const runUploadPreview = async () => {
    if (!uploadFile) { toast.error("Dosya seçin"); return; }
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("year", String(uploadYear));
    fd.append("preview", "true");
    setUploadLoading(true);
    try {
      const { data } = await api.post("/vehicle-earnings/upload-yearly-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadPreview(data);
      toast.success(`${data.totals?.vehicles || 0} araç, ${data.totals?.matched || 0} eşleşti`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploadLoading(false); }
  };
  const runUploadCommit = async () => {
    if (!uploadFile) return;
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("year", String(uploadYear));
    fd.append("preview", "false");
    fd.append("replace_year", replaceYear ? "true" : "false");
    setUploadLoading(true);
    try {
      const { data } = await api.post("/vehicle-earnings/upload-yearly-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} yeni · ${data.updated} güncelleme${data.skipped_no_vehicle ? ` · ${data.skipped_no_vehicle} atlandı (araç yok)` : ""}`);
      setShowUpload(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploadLoading(false); }
  };

  // Monthly details handlers
  const openDetails = () => { setDetFile(null); setDetPreview(null); setDetReplace(false); setShowDetails(true); };
  const runDetPreview = async () => {
    if (!detFile) { toast.error("Dosya seçin"); return; }
    const fd = new FormData();
    fd.append("file", detFile);
    fd.append("preview", "true");
    setDetLoading(true);
    try {
      const { data } = await api.post("/vehicle-earnings/upload-monthly-details", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDetPreview(data);
      toast.success(`${data.totals?.blocks || 0} araç × ay bloğu okundu`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setDetLoading(false); }
  };
  const runDetCommit = async () => {
    if (!detFile) return;
    const fd = new FormData();
    fd.append("file", detFile);
    fd.append("preview", "false");
    fd.append("replace", detReplace ? "true" : "false");
    setDetLoading(true);
    try {
      const { data } = await api.post("/vehicle-earnings/upload-monthly-details", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created_invoices} fatura · ${data.created_expenses} gider · ${data.created_earnings} kazanç · ${data.created_companies} yeni firma`);
      setShowDetails(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setDetLoading(false); }
  };

  const vehiclePlate = (id) => vehicles.find(v => v.id === id)?.plate || "-";
  const vehicleModel = (id) => vehicles.find(v => v.id === id)?.model || "";

  const totals = summary.totals || {};
  const monthly = (summary.monthly || []).map(m => ({ ...m, label: monthLabel(m.month) }));

  // Per-vehicle aggregation for the current view
  const byVehicle = {};
  earnings.forEach(e => {
    byVehicle[e.vehicle_id] = (byVehicle[e.vehicle_id] || 0) + Number(e.amount || 0);
  });
  const vehicleRows = Object.entries(byVehicle)
    .map(([vid, amt]) => ({ vid, plate: vehiclePlate(vid), model: vehicleModel(vid), total: amt }))
    .sort((a, b) => b.total - a.total);

  return (
    <>
      <PageHeader
        title="Aylık Kazançlar"
        subtitle="Araç kazançları + kesilen faturalar − tedarikçi faturaları − ekstra giderler = net kazanç."
        testid="earnings-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {earnings.length > 0 && (
              <ExportButtons
                filename="aylik_kazanclar"
                title="Aylık Kazançlar"
                subtitle={`Net: ${formatTL(totals.net || 0)} · Araç: ${formatTL(totals.vehicle_earnings || 0)} · Kesilen: ${formatTL(totals.sales_invoices || 0)} · Tedarikçi: ${formatTL(totals.supplier_invoices || 0)} · Ekstra: ${formatTL(totals.extra_expenses || 0)}`}
                sections={[
                  {
                    name: "Aylık Özet", heading: "Aylık Özet",
                    columns: [
                      { key: "month", label: "Ay" },
                      { key: "vehicle_earnings", label: "Araç Kazancı", format: "money" },
                      { key: "sales_invoices", label: "Kesilen Fatura", format: "money" },
                      { key: "supplier_invoices", label: "Tedarikçi Fat.", format: "money" },
                      { key: "extra_expenses", label: "Ekstra Gider", format: "money" },
                      { key: "net", label: "Net", format: "money" },
                    ],
                    rows: summary.monthly || [],
                  },
                  {
                    name: "Araç Kazançları", heading: "Araç Kazanç Kayıtları",
                    columns: [
                      { key: "month", label: "Dönem" },
                      { key: "vehicle_id", label: "Araç", get: (r) => vehiclePlate(r.vehicle_id) },
                      { key: "description", label: "Açıklama" },
                      { key: "amount", label: "Tutar", format: "money" },
                    ],
                    rows: earnings,
                  },
                ]}
              />
            )}
            <button
              data-testid="earnings-upload-btn"
              onClick={openUpload}
              className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white transition-colors"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Yıllık Excel
            </button>
            <button
              data-testid="earnings-details-btn"
              onClick={openDetails}
              className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-white transition-colors"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" /> Aylık Kar-Zarar Detay
            </button>
            <button
              data-testid="earnings-add-btn"
              onClick={openCreate}
              className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors"
            >
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Araç Kazancı Ekle
            </button>
          </div>
        }
      />

      {/* Summary KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KPICard testid="kpi-vehicle-earnings" label="Araç Kazançları" value={formatTL(totals.vehicle_earnings)} sub="Toplam" accent="info" icon={Truck} />
        <KPICard testid="kpi-sales-invoices" label="Kesilen Faturalar" value={formatTL(totals.sales_invoices)} sub="Firmalara" accent="success" icon={TrendingUp} />
        <KPICard testid="kpi-supplier-invoices" label="Tedarikçi Faturaları" value={formatTL(totals.supplier_invoices)} sub="Çıkarılacak" accent="error" icon={TrendingDown} />
        <KPICard testid="kpi-extra-expenses" label="Ekstra Giderler" value={formatTL(totals.extra_expenses || 0)} sub="Çıkarılacak" accent="error" icon={TrendingDown} />
        <KPICard testid="kpi-net-earnings" label="Net Kazanç" value={formatTL(totals.net)} sub="Gelir − Giderler" accent={(totals.net || 0) >= 0 ? "success" : "error"} icon={Wallet} />
      </div>

      {/* Monthly summary chart */}
      <div className="bg-white border border-[#E5E5E5] p-5 rounded-md mb-6" data-testid="earnings-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Aylık Net Kazanç Grafiği</div>
            <div className="text-lg font-bold font-heading mt-1">Kazanç − Tedarikçi Faturaları</div>
          </div>
        </div>
        {monthly.length === 0 ? (
          <div className="h-[240px] flex items-center justify-center text-sm text-neutral-500">Henüz veri yok — Araç kazancı veya kesilen fatura ekleyin.</div>
        ) : (
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#525252" }} />
                <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickFormatter={(v) => new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="vehicle_earnings" name="Araç Kazancı" stackId="in" fill="#0020C2" />
                <Bar dataKey="sales_invoices" name="Kesilen Fatura" stackId="in" fill="#16A34A" />
                <Bar dataKey="supplier_invoices" name="Tedarikçi Fatura" stackId="out" fill="#DC2626" />
                <Bar dataKey="extra_expenses" name="Ekstra Gider" stackId="out" fill="#F59E0B" />
                <Bar dataKey="net" name="Net Kazanç" fill="#0A0A0A">
                  {monthly.map((m, i) => <Cell key={i} fill={m.net >= 0 ? "#0A0A0A" : "#DC2626"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Monthly detail table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="monthly-detail-table">
          <div className="px-5 py-4 border-b border-[#E5E5E5]">
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Aylık Detay</div>
            <div className="text-lg font-bold font-heading mt-1">Ay Bazlı Kazanç Kırılımı</div>
          </div>
          <div className="overflow-x-auto data-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {["Ay","Araç Kazancı","Kesilen Fatura","Tedarikçi","Ekstra","Net"].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthly.length === 0 ? (
                  <tr><td colSpan={6} className="p-10 text-center text-sm text-neutral-500">Henüz kayıt yok</td></tr>
                ) : monthly.slice().reverse().map(m => (
                  <tr key={m.month} className="border-b border-[#F5F5F5]">
                    <td className="px-4 py-3 font-medium">{m.label}</td>
                    <td className="px-4 py-3 text-right num">{formatTL(m.vehicle_earnings)}</td>
                    <td className="px-4 py-3 text-right num text-[#16A34A]">{formatTL(m.sales_invoices)}</td>
                    <td className="px-4 py-3 text-right num text-[#DC2626]">−{formatTL(m.supplier_invoices)}</td>
                    <td className="px-4 py-3 text-right num text-[#F59E0B]">−{formatTL(m.extra_expenses || 0)}</td>
                    <td className={`px-4 py-3 text-right num font-semibold ${m.net >= 0 ? "text-[#0A0A0A]" : "text-[#DC2626]"}`}>{formatTL(m.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="vehicle-total-table">
          <div className="px-5 py-4 border-b border-[#E5E5E5]">
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Araç Bazlı Toplam</div>
            <div className="text-lg font-bold font-heading mt-1">En Çok Kazandıran Araçlar</div>
          </div>
          <div className="p-2">
            {vehicleRows.length === 0 ? (
              <div className="p-8 text-center text-sm text-neutral-500">Henüz araç kazancı yok</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {vehicleRows.map((v, i) => (
                    <tr key={v.vid} className="border-b border-[#F5F5F5]">
                      <td className="px-3 py-2.5 text-neutral-400 num text-xs w-8">{i + 1}</td>
                      <td className="px-2 py-2.5"><div className="num font-semibold">{v.plate}</div><div className="text-[10px] text-neutral-500">{v.model || "-"}</div></td>
                      <td className="px-3 py-2.5 text-right num font-semibold">{formatTL(v.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Vehicle earnings list */}
      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid="earnings-table">
        <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Araç Kazançları</div>
            <div className="text-lg font-bold font-heading mt-1">Kayıtlar</div>
          </div>
        </div>
        {earnings.length === 0 ? (
          <div className="p-12 text-center">
            <div className="text-neutral-500 text-sm mb-3">Henüz araç kazancı girilmemiş.</div>
            <button onClick={openCreate} className="text-xs uppercase tracking-widest font-semibold text-[#0020C2] hover:underline">İlk kaydı oluştur →</button>
          </div>
        ) : (
          <div className="overflow-x-auto data-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {["Dönem","Araç","Açıklama","Tutar",""].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 3 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {earnings.map(r => (
                  <tr key={r.id} className="border-b border-[#F5F5F5]" data-testid={`earning-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium">{monthLabel(r.month)}</td>
                    <td className="px-4 py-3"><span className="num font-semibold">{vehiclePlate(r.vehicle_id)}</span> <span className="text-xs text-neutral-500">{vehicleModel(r.vehicle_id)}</span></td>
                    <td className="px-4 py-3 text-neutral-600">{r.description || "-"}</td>
                    <td className="px-4 py-3 text-right num font-semibold">{formatTL(r.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`earning-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`earning-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">Araç Kazancı</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Araç *</label>
                <select data-testid="earning-field-vehicle" required value={form.vehicle_id} onChange={(e) => setForm({...form, vehicle_id: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  <option value="">Seçiniz</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate}{v.model ? ` — ${v.model}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Dönem (YYYY-MM) *</label>
                <input data-testid="earning-field-month" type="month" required value={form.month} onChange={(e) => setForm({...form, month: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input data-testid="earning-field-amount" type="number" step="any" required value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Açıklama</label>
                <textarea data-testid="earning-field-desc" rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="earning-submit-btn" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Yearly XLSX Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım</div>
                <div className="font-heading font-bold text-lg">Yıllık Araç Kazançları Excel Yükleme</div>
              </div>
              <button onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="upload-close-btn"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700 leading-relaxed">
                <div className="font-semibold text-[#0A0A0A] mb-2">📋 Beklenen Excel formatı:</div>
                <div><span className="font-semibold">Sayfa adı:</span> <span className="num">YILLIK TAKİP</span> (varsa otomatik seçilir, yoksa aktif sayfa)</div>
                <div><span className="font-semibold">Sütunlar:</span> Plaka · Ocak · Şubat · Mart · Nisan · Mayıs · Haziran · Temmuz · Ağustos · Eylül · Ekim · Kasım · Aralık · (Yıllık Toplam)</div>
                <div className="mt-2 text-neutral-500">Sistem plakayı sistemdeki araçlarla eşleştirir (boşluk/büyük-küçük harf farkı önemsiz). Eşleşmeyen plakalar atlanır ve raporda gösterilir. Her (araç, ay) için tek kayıt tutulur — mevcutsa güncellenir, yoksa oluşturulur.</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yıl *</label>
                  <input type="number" min="2000" max="2100" value={uploadYear} onChange={(e) => { setUploadYear(Number(e.target.value)); setUploadPreview(null); }}
                    className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" data-testid="upload-year" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                  <input type="file" accept=".xlsx" onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadPreview(null); }}
                    className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" data-testid="upload-file" />
                </div>
              </div>

              {!uploadPreview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={runUploadPreview} disabled={uploadLoading || !uploadFile}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40" data-testid="upload-preview-btn">
                    {uploadLoading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {uploadPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Araç</div>
                      <div className="num font-bold text-lg">{uploadPreview.totals?.vehicles || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eşleşen</div>
                      <div className="num font-bold text-lg text-[#16A34A]">{uploadPreview.totals?.matched || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Eşleşmeyen</div>
                      <div className="num font-bold text-lg text-[#DC2626]">{uploadPreview.totals?.unmatched || 0}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Yıllık Toplam</div>
                      <div className="num font-bold text-base">{formatTL(uploadPreview.totals?.yearly_total || 0)}</div>
                    </div>
                  </div>

                  {uploadPreview.totals?.unmatched > 0 && (
                    <div className="border border-[#F59E0B] bg-[#FEF3C7] p-3 text-xs flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-[#0A0A0A]">Bazı plakalar sistemde bulunamadı:</div>
                        <div className="mt-1 num text-neutral-700">
                          {uploadPreview.rows.filter(r => !r.vehicle_matched).map(r => r.plate).join(", ")}
                        </div>
                        <div className="mt-1 text-neutral-500">Bu araçlar atlanacak. Önce <span className="font-semibold">Araçlar</span> sayfasından eklerseniz bir sonraki yüklemede otomatik eşleşir.</div>
                      </div>
                    </div>
                  )}

                  <div className="border border-[#E5E5E5] rounded overflow-x-auto max-h-[350px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Plaka</th>
                          <th className="px-2 py-2 text-center text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">✓</th>
                          {["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"].map(m => (
                            <th key={m} className="px-1.5 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{m}</th>
                          ))}
                          <th className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Yıllık</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(uploadPreview.rows || []).map((r, i) => (
                          <tr key={i} className={`border-t border-[#F5F5F5] ${!r.vehicle_matched ? "bg-[#FEF3C7]" : ""}`}>
                            <td className="px-2 py-1.5 num font-semibold">{r.plate}</td>
                            <td className="px-2 py-1.5 text-center">{r.vehicle_matched ? <span className="text-[#16A34A]">✓</span> : <span className="text-[#DC2626]">✕</span>}</td>
                            {r.months.map((m, j) => (
                              <td key={j} className="px-1.5 py-1.5 text-right num text-[10px] text-neutral-600">{m.amount ? formatNumber(m.amount) : "-"}</td>
                            ))}
                            <td className="px-2 py-1.5 text-right num font-semibold">{formatTL(r.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input id="replace-year" type="checkbox" checked={replaceYear} onChange={(e) => setReplaceYear(e.target.checked)} data-testid="upload-replace-check" />
                    <label htmlFor="replace-year" className="text-xs text-neutral-700 cursor-pointer">
                      <span className="font-semibold">{uploadYear} yılının mevcut kayıtlarını sil ve baştan yükle</span> <span className="text-neutral-500">(kapalıyken sadece yeni ekler + günceller)</span>
                    </label>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setUploadPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowUpload(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button onClick={runUploadCommit} disabled={uploadLoading}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40" data-testid="upload-commit-btn">
                        {uploadLoading ? "Kaydediliyor..." : `✓ ${uploadPreview.totals?.matched || 0} Araç için Kaydet`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Monthly Details Upload Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowDetails(false)}>
          <div className="bg-white w-full max-w-5xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplu İçe Aktarım — Detaylı</div>
                <div className="font-heading font-bold text-lg">Aylık Araç Kar-Zarar Detay Excel</div>
              </div>
              <button onClick={() => setShowDetails(false)} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="details-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-4 text-xs text-neutral-700">
                <div className="font-semibold text-[#0A0A0A] mb-1">📋 Beklenen sekme formatı:</div>
                <div>Her aylık sekme (<span className="num">2025 OCAK, 2025 ŞUBAT, ...</span>) — Her araç 4 satırda:</div>
                <div className="ml-3 mt-1 num text-neutral-600">
                  <div>Satır 1: [Plaka] · KESİLEN FATURALAR · MASRAF KALEMLERİ</div>
                  <div>Satır 2: FİRMA 1..5 · OKUL · TOPLAM | ŞOFÖR MAL · HOSTES MAL · YAKIT · TAMİR · KASKO · DİĞER · TOPLAM | ARAÇ KAR/ZARAR</div>
                  <div>Satır 3: (firma isimleri)</div>
                  <div>Satır 4: (tutarlar)</div>
                </div>
                <div className="mt-2 text-neutral-500">Sistem şunları oluşturur: her firma için <b>satış faturası</b>, her masraf kalemi için <b>ekstra gider</b>, aylık araç <b>kazanç kaydı</b>. Bulunmayan firmalar otomatik açılır.</div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Excel Dosyası *</label>
                <input type="file" accept=".xlsx" onChange={e => { setDetFile(e.target.files?.[0] || null); setDetPreview(null); }}
                  className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" data-testid="details-file" />
              </div>

              {!detPreview && (
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={runDetPreview} disabled={detLoading || !detFile}
                    className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] disabled:opacity-40" data-testid="details-preview-btn">
                    {detLoading ? "Okunuyor..." : "Önizle"}
                  </button>
                </div>
              )}

              {detPreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Blok (Araç × Ay)</div>
                      <div className="num font-bold text-lg">{detPreview.totals?.blocks || 0}</div>
                      <div className="text-[10px] text-neutral-500">{detPreview.totals?.matched}/{detPreview.totals?.blocks} eşleşti</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Satış Toplam</div>
                      <div className="num font-bold text-base text-[#0020C2]">{formatTL(detPreview.totals?.sales_total || 0)}</div>
                      <div className="text-[10px] text-neutral-500">{detPreview.totals?.invoice_rows} fatura satırı</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Gider Toplam</div>
                      <div className="num font-bold text-base text-[#DC2626]">{formatTL(detPreview.totals?.costs_total || 0)}</div>
                      <div className="text-[10px] text-neutral-500">{detPreview.totals?.expense_rows} gider kalemi</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Net Kar</div>
                      <div className={`num font-bold text-base ${(detPreview.totals?.net || 0) >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                        {formatTL(detPreview.totals?.net || 0)}
                      </div>
                    </div>
                  </div>

                  <div className="border border-[#E5E5E5] rounded overflow-hidden max-h-[350px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FAFAFA] sticky top-0">
                        <tr>
                          {["Ay","Plaka","✓","Firmalar","Satış","Giderler","Net"].map((h, i) => (
                            <th key={h} className={`px-2 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i > 3 ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(detPreview.blocks || []).map((b, i) => (
                          <tr key={i} className={`border-t border-[#F5F5F5] ${!b.plate_matched ? "bg-[#FEF3C7]" : ""}`}>
                            <td className="px-2 py-1.5 num">{b.month}</td>
                            <td className="px-2 py-1.5 num font-semibold">{b.plate}</td>
                            <td className="px-2 py-1.5">{b.plate_matched ? <span className="text-[#16A34A]">✓</span> : <span className="text-[#DC2626]">✕</span>}</td>
                            <td className="px-2 py-1.5 text-[10px]">{(b.firms||[]).map(f => f.firm).join(", ")}</td>
                            <td className="px-2 py-1.5 text-right num text-[#0020C2] font-semibold">{formatTL(b.total_sales)}</td>
                            <td className="px-2 py-1.5 text-right num text-[#DC2626]">{formatTL(b.total_costs)}</td>
                            <td className={`px-2 py-1.5 text-right num font-bold ${b.kar_zarar >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{formatTL(b.kar_zarar)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input id="det-replace" type="checkbox" checked={detReplace} onChange={e => setDetReplace(e.target.checked)} data-testid="details-replace-check" />
                    <label htmlFor="det-replace" className="text-xs text-neutral-700 cursor-pointer">
                      <span className="font-semibold">Aynı araç × ay için mevcut kayıtları silip yeniden yaz</span> <span className="text-neutral-500">(kapalıyken var olanlar atlanır)</span>
                    </label>
                  </div>

                  <div className="flex justify-between items-center gap-2 pt-4 border-t border-[#E5E5E5]">
                    <button onClick={() => setDetPreview(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">← Farklı Dosya</button>
                    <div className="flex gap-2">
                      <button onClick={() => setShowDetails(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                      <button onClick={runDetCommit} disabled={detLoading}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#0F7A36] disabled:opacity-40" data-testid="details-commit-btn">
                        {detLoading ? "Kaydediliyor..." : `✓ ${detPreview.totals?.matched || 0} Blok İşle`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
